/*

  Searchbutton.ts

  Search button component

  Copyright (C) 2024 Miðeind ehf.
  Author: Vilhjalmur Thorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { IView } from "./types";
import { ts } from "./i18n";
import { ComponentFunc, m } from "./mithril";
import { glyph, nbsp } from "./util";

interface IAttributes {
  view: IView;
}

export const SearchButton: ComponentFunc<IAttributes> = (initialVnode) => {

  // A combination of a button and pattern entry field
  // for user search

  const view = initialVnode.attrs.view;
  const model = view.model;
  let spec = ""; // The current search pattern
  let promise: { result: boolean; p: Promise<boolean>; } | undefined = undefined;

  function newSearch() {
    // There may have been a change of search parameters: react
    if (promise !== undefined) {
      // There was a previous promise, now obsolete: make it
      // resolve without action
      promise.result = false;
      promise = undefined;
    }
    let sel = model.userListCriteria?.query || "robots";
    if (sel !== "search") {
      // Not already in a search: load the user list immediately
      view.actions.searchUsers(spec, true);
      return;
    }
    if (spec === model.userListCriteria?.spec)
      // We're already looking at the same search spec: done
      return;
    // We're changing the search spec.
    // In order to limit the number of search queries sent to
    // the server while typing a new criteria, we keep an
    // outstanding promise that resolves in 0.8 seconds,
    // unless cancelled by a new keystroke/promise.
    // Note: since a promise can't be directly cancelled, we use a
    // convoluted route to associate a boolean result with it.
    let newP = {
      result: true,
      p: new Promise<boolean>((resolve) => {
        // After 800 milliseconds, resolve to whatever value the
        // result property has at that time. It will be true
        // unless the promise has been "cancelled" by setting
        // its result property to false.
        setTimeout(() => { resolve(newP.result); }, 800);
      })
    };
    promise = newP;
    promise.p.then((value: boolean) => {
      if (value) {
        // Successfully resolved, without cancellation:
        // issue the search query to the server as it now stands
        view.actions.searchUsers(spec, true);
        promise = undefined;
      }
    });
  }

  return {

    view: () => {
      const sel = model.userListCriteria?.query || "robots";
      return m(".user-cat[id='user-search']",
        [
          glyph("search",
            {
              id: 'search',
              className: (sel == "search" ? "shown" : ""),
              onclick: () => {
                // Reset the search pattern when clicking the search icon
                spec = "";
                newSearch();
                document.getElementById("search-id")?.focus();
              }
            }
          ),
          nbsp(),
          m("input.text.userid",
            {
              type: 'text',
              id: 'search-id',
              name: 'search-id',
              maxlength: 16,
              placeholder: ts('Einkenni eða nafn'),
              value: spec,
              onfocus: () => newSearch(),
              oninput: (ev: Event) => {
                spec = (ev.target as HTMLInputElement).value + "";
                newSearch();
              }
            }
          )
        ]
      );
    }
  };
};
